import React, { useMemo } from 'react';
import classNames from 'classnames';
import {
  DATA_ROOM_NODE_ACCESS_STATES,
  DataRoomNodeWithStateAndSignal,
  Lead,
} from 'models/Lead';
import useCallbackAsync from 'hooks/useCallbackAsync';
import api from 'api';
import { Loader } from 'ncoded-component-library';
import SoftSignalIcon from 'components/SoftSignalIcon';
import utils from 'utils';
import ResolvePopup from 'components/ResolvePopup';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import { QuickFileReleaseInitiatedEventProps } from 'models/MixpanelEvents';
import { useTranslation } from 'react-i18next';
import useIsTextOverflowing from 'hooks/useIsTextOverflowing';

import './ReleaseFileInstance.styles.scss';

const MAX_LENGTH = 15;

type ReleaseFileInstanceProps = {
  className?: string;
  leadId: string;
  disabled?: boolean;
  dealRoomId: string;
  dataRoomNodeWithStateAndSignal: DataRoomNodeWithStateAndSignal;
  onSuccessRelease?: (lead: Lead) => void;
};

const ReleaseFileInstance: React.FC<ReleaseFileInstanceProps> = (props) => {
  const {
    className,
    leadId,
    dealRoomId,
    dataRoomNodeWithStateAndSignal,
    disabled = false,
    onSuccessRelease,
  } = props;

  const { dataRoomNodeId, state, softSignal, dataRoomNode } =
    dataRoomNodeWithStateAndSignal;

  const { name = '' } = dataRoomNode || {};

  const { t } = useTranslation();

  const getMixpanelProps = useGetMixpanelProps();

  const { isOverflowing, spanRef } = useIsTextOverflowing();

  const [releaseFile, isLoading] = useCallbackAsync(async () => {
    mixpanelService.quickFileReleaseInitiated(
      getMixpanelProps({
        dealId: dealRoomId,
        fileName: name,
        action: t(
          state === DATA_ROOM_NODE_ACCESS_STATES.NO_ACCESS
            ? 'grantAccess'
            : 'revokeAccess',
        ),
      }) as QuickFileReleaseInitiatedEventProps,
    );

    const { data } = await api.portfolio.seller.releaseFileForLead(
      leadId,
      dataRoomNodeId,
      state === DATA_ROOM_NODE_ACCESS_STATES.NO_ACCESS,
    );
    onSuccessRelease?.(data);
  });

  const canRelease = useMemo(
    () => !isLoading && !disabled,
    [disabled, isLoading],
  );

  const classes = classNames(
    'release-file-instance',
    state,
    {
      clickable: canRelease,
    },
    className,
  );

  const isNameTruncated = useMemo(() => name.length > MAX_LENGTH, [name]);

  const displayName = useMemo(
    () => (isNameTruncated ? utils.getShortenedString(name, MAX_LENGTH) : name),
    [isNameTruncated, name],
  );

  const fileContent = useMemo(
    () => (
      <div
        className={classes}
        {...(canRelease && {
          onClick: (e) => {
            e.stopPropagation();
            releaseFile();
          },
        })}
      >
        {isLoading && <Loader size="small" />}

        <span className="release-file-instance__name" ref={spanRef}>
          {displayName}
        </span>

        <SoftSignalIcon softSignal={softSignal} />
      </div>
    ),
    [
      canRelease,
      classes,
      displayName,
      isLoading,
      softSignal,
      spanRef,
      releaseFile,
    ],
  );

  if (isOverflowing)
    return (
      <ResolvePopup
        onHover
        popupContent={name}
        className="release-file-instance__popup"
      >
        {fileContent}
      </ResolvePopup>
    );

  return fileContent;
};

export default ReleaseFileInstance;
